import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, Navigate, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Chip, CircularProgress, Stack, Tab, Tabs, Typography } from "@mui/material";
import { T, TYPE } from "../user/complaints/shared";
import { useRoom, useSocketEvent } from "../helpdesk/socket";
import { pm, errorMessage } from "./api";
import { PmPage } from "./PmContext";
import { ProjectBadge, has } from "./ui";
import PmTopBar from "./PmTopBar";
import IssueDetail from "./IssueDetail";
import Overview from "./project/Overview";
import BoardsTab from "./boards/BoardsTab";
import BoardShell from "./boards/BoardShell";
import IssueNavigator from "./IssueNavigator";
import Sprints from "./project/Sprints";
import Roadmap from "./project/Roadmap";
import Releases from "./project/Releases";
import ProjectReports from "./project/ProjectReports";
import Activity from "./project/Activity";
import Files from "./project/Files";
import Discussion from "./project/Discussion";
import Docs from "./project/Docs";
import ProjectSettings from "./project/ProjectSettings";

// Tabs are permission-aware: a tab shows only when the role can view it.
const TABS = [
  { key: "overview", label: "Overview", perm: "project.view" },
  // Board / Backlog live inside each board (Boards → board → Active sprint / Backlog …);
  // old /board and /backlog links still redirect to the default board below.
  { key: "boards", label: "Boards", perm: "board.view" },
  { key: "issues", label: "Issues", perm: "issue.view" },
  { key: "sprints", label: "Sprints", perm: "sprint.view", sprintsOnly: true },
  { key: "roadmap", label: "Roadmap", perm: "roadmap.view" },
  { key: "releases", label: "Releases", perm: "release.view" },
  { key: "reports", label: "Reports", perm: "report.view" },
  { key: "activity", label: "Activity", perm: "project.view" },
  { key: "files", label: "Files", perm: "issue.view" },
  { key: "discussion", label: "Discussion", perm: "project.view" },
  { key: "docs", label: "Docs", perm: "project.view" },
  { key: "settings", label: "Settings", perm: "project.view" },
];

const Inner = () => {
  const { key, tab = "overview", issueKey, boardId, view } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0); // bump to refresh the active tab

  const load = useCallback(async () => {
    try {
      setProject(await pm.project(key));
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Project not found or you do not have access"));
    }
  }, [key]);
  useEffect(() => {
    load();
  }, [load]);

  useRoom("project", project?._id);
  useSocketEvent(["project.updated", "project.member.added", "project.member.removed"], (p) => {
    if (!p?._id || String(p._id) === String(project?._id)) load();
  });

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!project) return <Box sx={{ py: 10, textAlign: "center" }}><CircularProgress size={26} /></Box>;

  const tabs = TABS.filter((t) => has(project, t.perm) && (!t.sprintsOnly || project.sprintsEnabled));
  const current = issueKey ? "issues" : boardId ? "boards" : tabs.some((t) => t.key === tab) ? tab : "overview";
  // "Board" / "Backlog" open the default board (mine, else the project default) inside Boards
  if (!issueKey && !boardId && (tab === "board" || tab === "backlog")) {
    const def = (project.boards || []).find((b) => String(b._id) === String(project.myDefaultBoardId)) || (project.boards || []).find((b) => b.isDefault) || project.boards?.[0];
    if (def) return <Navigate replace to={`/projects/${project.key}/boards/${def._id}${tab === "backlog" ? "/backlog" : ""}`} />;
  }
  const refresh = () => setVersion((v) => v + 1);
  const props = { project, reloadProject: load, version, refresh };

  return (
    <>
      <PmTopBar project={project} onCreated={refresh} />
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <ProjectBadge project={project} size={40} />
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography component={RouterLink} to="/projects" sx={{ fontSize: TYPE.micro, color: T.inkMuted, textDecoration: "none", "&:hover": { color: T.brand } }}>Projects</Typography>
            <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>/ {project.key}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography noWrap sx={{ fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{project.name}</Typography>
            {project.archivedAt && <Chip size="small" label="Archived · read only" sx={{ height: 22 }} />}
            {project.myRole && <Chip size="small" variant="outlined" label={project.myRole} sx={{ height: 22, fontSize: TYPE.micro }} />}
          </Stack>
        </Box>
      </Stack>
      <Tabs value={current} onChange={(_, v) => navigate(`/projects/${project.key}/${v}`)} variant="scrollable" scrollButtons="auto"
        sx={{ borderBottom: `1px solid ${T.border}`, mb: 2.5, minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600, fontSize: TYPE.small, px: 1.5, minWidth: 0 } }}>
        {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>
      {issueKey ? (
        <IssueDetail key={issueKey} issueKey={issueKey} onChanged={refresh} />
      ) : (
        {
          overview: <Overview {...props} />,
          boards: boardId ? <BoardShell project={project} boardId={boardId} view={view} /> : <BoardsTab {...props} />,
          issues: <IssueNavigator {...props} />,
          sprints: <Sprints {...props} />,
          roadmap: <Roadmap {...props} />,
          releases: <Releases {...props} />,
          reports: <ProjectReports {...props} />,
          activity: <Activity {...props} />,
          files: <Files {...props} />,
          discussion: <Discussion {...props} />,
          docs: <Docs {...props} />,
          settings: <ProjectSettings {...props} />,
        }[current]
      )}
    </>
  );
};

const ProjectLayout = () => {
  const { boardId } = useParams();
  return (
    <PmPage maxWidth={boardId ? false : "xl"}>
      <Inner />
    </PmPage>
  );
};

export default ProjectLayout;
